import type { Game } from './Game';
import type { GameStateManager } from './GameState';
import type { GameData, OrderType, LocationId, ResearchTypeId } from './types';
import type { UIManager } from '../ui/UIManager';

export class GameController {
  constructor(
    private game: Game,
    private gameState: GameStateManager,
    private gameData: GameData,
    private uiManager: UIManager
  ) {}

  handleOrder(order: OrderType | 'deselect' | 'cancel_synthetize' | 'cancel_rest', ...args: unknown[]): void {
    const orderSystem = this.game.getOrderSystem();
    const selected = this.gameState.getSelectedEntity();

    if (order === 'deselect') {
      this.gameState.setSelectedEntity(null, null);
      this.uiManager.update();
      return;
    }

    if (selected.type === 'researcher') {
      const researcher = this.gameState.getResearcher(selected.id as string);
      if (!researcher) {
        return;
      }

      switch (order) {
        case 'move':
          // Move order now comes with targetLocation from right-click
          if (args.length >= 1) {
            const targetLocation = args[0] as LocationId;
            if (orderSystem.issueMoveOrder(researcher, targetLocation)) {
              this.showOrderConfirmation('move');
            }
          } else {
            // Fallback: if no target provided, set pending move (for compatibility)
            this.gameState.setPendingMoveOrder({ researcherId: selected.id });
          }
          break;
        case 'research':
          // Research order comes with researcherId, types and cellId
          if (args.length >= 3) {
            const researcherId = args[0] as string;
            const types = args[1] as ResearchTypeId[];
            const cellId = args[2] as LocationId;
            // Get the specific researcher by ID (not the selected one)
            const targetResearcher = this.gameState.getResearcher(researcherId);
            if (!targetResearcher) {
              console.error(`Research order failed: Researcher ${researcherId} not found`);
              break;
            }
            if (orderSystem.issueResearchOrder(targetResearcher, cellId, types)) {
              this.showOrderConfirmation('research');
            } else {
              console.warn(`Research order failed for researcher ${targetResearcher.data.name} at ${cellId}`, {
                types,
                location: targetResearcher.location,
                currentAction: targetResearcher.currentAction,
                silverBullet: targetResearcher.silverBullet
              });
            }
          } else if (args.length >= 2) {
            // Fallback: old format with just types and cellId (use selected researcher)
            const types = args[0] as ResearchTypeId[];
            const cellId = args[1] as LocationId;
            if (orderSystem.issueResearchOrder(researcher, cellId, types)) {
              this.showOrderConfirmation('research');
            } else {
              console.warn(`Research order failed for researcher ${researcher.data.name} at ${cellId}`, {
                types,
                location: researcher.location,
                currentAction: researcher.currentAction,
                silverBullet: researcher.silverBullet
              });
            }
          }
          break;
        case 'accept':
          if (args[0]) {
            orderSystem.issueAcceptOrder(researcher, args[0] as LocationId);
            this.showOrderConfirmation('accept');
          }
          break;
        case 'coredump':
          if (args[0]) {
            orderSystem.issueCoredumpOrder(researcher, args[0] as LocationId);
            this.showOrderConfirmation('coredump');
          }
          break;
        case 'craft':
          // Craft order comes with bulletId
          if (args.length >= 1) {
            const bulletId = args[0] as string;
            if (orderSystem.issueCraftOrder(researcher, bulletId)) {
              this.showOrderConfirmation('craft');
            }
          }
          break;
        case 'recruit':
          // Recruit order is issued when modal confirms
          if (orderSystem.issueRecruitOrder(researcher)) {
            this.showOrderConfirmation('recruit');
          }
          break;
        case 'synthetize':
          orderSystem.issueSynthetizeOrder(researcher);
          this.showOrderConfirmation('synthetize');
          break;
        case 'cancel_synthetize':
          orderSystem.cancelOngoingAction(researcher);
          this.showOrderConfirmation('cancel');
          break;
        case 'rest':
          orderSystem.issueRestOrder(researcher);
          this.showOrderConfirmation('rest');
          break;
        case 'cancel_rest':
          orderSystem.cancelOngoingAction(researcher);
          this.showOrderConfirmation('cancel');
          break;
        case 'contain':
          if (args[0]) {
            if (orderSystem.issueContainOrder(researcher, args[0] as LocationId)) {
              this.showOrderConfirmation('contain');
            }
          }
          break;
      }
    }

    this.uiManager.update();
  }

  setupCellClickHandling(container: HTMLElement): void {
    // Use event delegation for cell clicks
    container.addEventListener('click', (e) => {
      const cellElement = (e.target as HTMLElement).closest('.cell-display');
      if (!cellElement) return;

      const pendingMove = this.gameState.getPendingMoveOrder();
      if (pendingMove) {
        const researcher = this.gameState.getResearcher(pendingMove.researcherId);
        if (!researcher) {
          this.gameState.setPendingMoveOrder(null);
          return;
        }

        // Extract cell ID from class list
        const cellId = cellElement.classList.contains('cell_1') ? 'cell_1' :
                      cellElement.classList.contains('cell_2') ? 'cell_2' :
                      cellElement.classList.contains('cell_3') ? 'cell_3' :
                      cellElement.classList.contains('cell_4') ? 'cell_4' :
                      cellElement.classList.contains('control_room') ? 'control_room' : null;
        
        if (cellId) {
          const orderSystem = this.game.getOrderSystem();
          if (orderSystem.issueMoveOrder(researcher, cellId)) {
            this.showOrderConfirmation('move');
            this.gameState.setPendingMoveOrder(null);
            this.uiManager.update();
          }
        }
      }
    });
  }

  private showOrderConfirmation(order: string): void {
    // Visual feedback for order received
    const confirmation = document.createElement('div');
    confirmation.className = 'order-confirmation';
    confirmation.textContent = 'Order Received';
    document.body.appendChild(confirmation);
    const uiConstants = this.gameData.uiConstants;
    setTimeout(() => {
      confirmation.classList.add('fade-out');
      setTimeout(() => confirmation.remove(), uiConstants.order_confirmation_fade_ms);
    }, uiConstants.order_confirmation_display_ms);
  }
}
